import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackCheckoutStarted, trackLoginCompleted, trackLoginDrawerOpened, trackSignupCompleted } from '../analytics';
import {
  AuthUser,
  LoginResult,
  fetchCurrentUser,
  login as apiLogin,
  loginWithGoogle as apiLoginWithGoogle,
  logout as apiLogout,
  register as apiRegister,
  verifyMfaLogin as apiVerifyMfaLogin,
} from '../api/auth';
import { createCheckoutSession, CHECKOUT_SESSION_STORAGE_KEY } from '../api/billing';

interface AuthContextValue {
  /** undefined = still checking the session, null = signed out. */
  user: AuthUser | null | undefined;
  /** Resolves with the signed-in user, or an MFA challenge to complete via verifyMfaLogin. */
  login: (email: string, password: string) => Promise<LoginResult>;
  register: (email: string, name: string, password: string) => Promise<AuthUser>;
  loginWithGoogle: (credential: string) => Promise<AuthUser>;
  /** Completes a password sign-in for accounts with two-factor authentication. */
  verifyMfaLogin: (mfaToken: string, code: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  isLoginOpen: boolean;
  /** Route to navigate to after a successful sign-in, e.g. "/decks/new?template=fintech-saas". */
  loginNext: string | null;
  openLogin: (next?: string) => void;
  closeLogin: () => void;
  /** Navigate when signed in; otherwise open the login drawer and defer navigation until after sign-in. */
  navigateWithAuth: (path: string) => void;
  /** Redirect to checkout (or apply an in-place upgrade when already subscribed). */
  subscribeToPlan: (plan: Exclude<AuthUser['plan'], 'free'>) => Promise<'redirect' | 'completed'>;
  refreshUser: () => Promise<void>;
  applyUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [loginNext, setLoginNext] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentUser()
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  const openLogin = useCallback((next?: string) => {
    setLoginNext(next ?? null);
    setIsLoginOpen(true);
    trackLoginDrawerOpened(next ?? null);
  }, []);

  const closeLogin = useCallback(() => {
    setIsLoginOpen(false);
  }, []);

  const navigateWithAuth = useCallback(
    (path: string) => {
      const normalized = path.startsWith('/') ? path : `/${path}`;
      if (user) {
        navigate(normalized);
        return;
      }
      if (user === null) {
        openLogin(normalized);
      }
    },
    [user, openLogin, navigate],
  );

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiLogin({ email, password });
    if (result.user) {
      setUser(result.user);
      trackLoginCompleted('email');
    }
    return result;
  }, []);

  const verifyMfaLogin = useCallback(async (mfaToken: string, code: string) => {
    const verified = await apiVerifyMfaLogin({ mfaToken, code });
    setUser(verified);
    trackLoginCompleted('mfa');
    return verified;
  }, []);

  const register = useCallback(async (email: string, name: string, password: string) => {
    const created = await apiRegister({ email, name, password });
    setUser(created);
    trackSignupCompleted('email');
    return created;
  }, []);

  const loginWithGoogle = useCallback(async (credential: string) => {
    const googleUser = await apiLoginWithGoogle(credential);
    setUser(googleUser);
    trackLoginCompleted('google');
    return googleUser;
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setIsLoginOpen(false);
    navigate('/', { replace: true });
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    setUser(await fetchCurrentUser());
  }, []);

  const applyUser = useCallback((nextUser: AuthUser) => {
    setUser(nextUser);
  }, []);

  const subscribeToPlan = useCallback(async (plan: Exclude<AuthUser['plan'], 'free'>) => {
    trackCheckoutStarted(plan);
    const { url, user: updatedUser, sessionId } = await createCheckoutSession(plan);
    if (updatedUser) {
      setUser(updatedUser);
      return 'completed';
    }
    if (sessionId) {
      sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_KEY, sessionId);
    }
    window.location.assign(url);
    return 'redirect';
  }, []);

  const value = useMemo(
    () => ({
      user,
      login,
      register,
      loginWithGoogle,
      verifyMfaLogin,
      logout,
      isLoginOpen,
      loginNext,
      openLogin,
      closeLogin,
      navigateWithAuth,
      subscribeToPlan,
      refreshUser,
      applyUser,
    }),
    [user, login, register, loginWithGoogle, verifyMfaLogin, logout, isLoginOpen, loginNext, openLogin, closeLogin, navigateWithAuth, subscribeToPlan, refreshUser, applyUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};
